<script setup>
import { reactive, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactAPI from 'dashboard/api/contacts';
import CustomerNotificationsAPI from 'dashboard/api/customerNotifications';

const emit = defineEmits(['sent']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);

const CATEGORIES = [
  { value: 'transactional', labelKey: 'CUSTOMER_NOTIFICATIONS.CATEGORY.TRANSACTIONAL', icon: 'i-lucide-credit-card' },
  { value: 'marketing', labelKey: 'CUSTOMER_NOTIFICATIONS.CATEGORY.MARKETING', icon: 'i-lucide-tag' },
  { value: 'system_alert', labelKey: 'CUSTOMER_NOTIFICATIONS.CATEGORY.SYSTEM_ALERT', icon: 'i-lucide-triangle-alert' },
  { value: 'operator_broadcast', labelKey: 'CUSTOMER_NOTIFICATIONS.CATEGORY.OPERATOR_BROADCAST', icon: 'i-lucide-megaphone' },
];

const AUDIENCES = [
  { value: 'all', labelKey: 'CUSTOMER_NOTIFICATIONS.CREATE.AUDIENCE.ALL', icon: 'i-lucide-users' },
  { value: 'contacts', labelKey: 'CUSTOMER_NOTIFICATIONS.CREATE.AUDIENCE.CONTACTS', icon: 'i-lucide-user' },
  { value: 'label', labelKey: 'CUSTOMER_NOTIFICATIONS.CREATE.AUDIENCE.LABEL', icon: 'i-lucide-tags' },
];

const initialState = {
  category: 'transactional',
  title: '',
  body: '',
  deepLink: '',
  tag: '',
  audienceType: 'contacts',
};

const state = reactive({ ...initialState });

const selectedContacts = ref([]); // array of { id, name, email, thumbnail, identifier }
const selectedLabels = ref([]); // array of label titles
const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const isSending = ref(false);
const audienceCount = ref(null);
const isCounting = ref(false);
const confirmed = ref(false);
let searchTimer = null;
let countTimer = null;

const TITLE_MAX = 200;
const BODY_MAX = 1000;

const labels = computed(() => store.getters['labels/getLabels'] || []);

const titleError = computed(() => {
  if (!state.title) return '';
  if (state.title.length > TITLE_MAX) return t('CUSTOMER_NOTIFICATIONS.CREATE.ERRORS.TITLE_TOO_LONG');
  return '';
});

const bodyError = computed(() => {
  if (!state.body) return '';
  if (state.body.length > BODY_MAX) return t('CUSTOMER_NOTIFICATIONS.CREATE.ERRORS.BODY_TOO_LONG');
  return '';
});

// A broadcast to everyone or a whole segment is a mass, irreversible action —
// gate it behind an explicit confirm click that shows the resolved count.
const needsConfirm = computed(() => state.audienceType !== 'contacts');

const hasRecipients = computed(() => {
  if (state.audienceType === 'contacts') return selectedContacts.value.length > 0;
  if (state.audienceType === 'label') return selectedLabels.value.length > 0;
  return true; // 'all'
});

const canSubmit = computed(
  () =>
    !!state.title.trim() &&
    !!state.body.trim() &&
    !!state.category &&
    hasRecipients.value &&
    !titleError.value &&
    !bodyError.value &&
    !isSending.value
);

const submitLabel = computed(() => {
  if (needsConfirm.value && confirmed.value) {
    return t('CUSTOMER_NOTIFICATIONS.CREATE.BUTTONS.CONFIRM_SEND', {
      n: audienceCount.value ?? '…',
    });
  }
  return t('CUSTOMER_NOTIFICATIONS.CREATE.BUTTONS.SEND');
});

const reset = () => {
  Object.assign(state, initialState);
  selectedContacts.value = [];
  selectedLabels.value = [];
  searchQuery.value = '';
  searchResults.value = [];
  audienceCount.value = null;
  confirmed.value = false;
};

const open = () => {
  reset();
  store.dispatch('labels/get');
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const runSearch = async query => {
  if (!query || query.trim().length < 1) {
    searchResults.value = [];
    return;
  }
  isSearching.value = true;
  try {
    const response = await ContactAPI.search(query.trim(), 1, 'name');
    const payload = response?.data?.payload || [];
    // Show only contacts with KC identifier (eligible recipients)
    searchResults.value = payload.filter(c => c.identifier && c.identifier.length > 0).slice(0, 15);
  } catch (e) {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const onSearchInput = value => {
  searchQuery.value = value;
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => runSearch(value), 250);
};

const isSelected = contact => selectedContacts.value.some(c => c.id === contact.id);

const toggleContact = contact => {
  if (isSelected(contact)) {
    selectedContacts.value = selectedContacts.value.filter(c => c.id !== contact.id);
  } else {
    selectedContacts.value = [...selectedContacts.value, contact];
  }
};

const removeContact = id => {
  selectedContacts.value = selectedContacts.value.filter(c => c.id !== id);
};

const isLabelSelected = title => selectedLabels.value.includes(title);

const toggleLabel = title => {
  selectedLabels.value = isLabelSelected(title)
    ? selectedLabels.value.filter(l => l !== title)
    : [...selectedLabels.value, title];
};

// Fetch the resolved recipient count for 'all' / 'label' modes so the sender
// sees the blast radius before confirming. Debounced against label changes.
const refreshCount = () => {
  if (state.audienceType === 'contacts') {
    audienceCount.value = null;
    return;
  }
  if (state.audienceType === 'label' && selectedLabels.value.length === 0) {
    audienceCount.value = 0;
    return;
  }
  isCounting.value = true;
  const params = { type: state.audienceType };
  if (state.audienceType === 'label') params.labels = selectedLabels.value;
  CustomerNotificationsAPI.audienceCount(params)
    .then(res => {
      audienceCount.value = res?.data?.count ?? 0;
    })
    .catch(() => {
      audienceCount.value = null;
    })
    .finally(() => {
      isCounting.value = false;
    });
};

// Any change to the audience invalidates a prior confirmation and the count.
watch(
  () => [state.audienceType, selectedLabels.value.slice()],
  () => {
    confirmed.value = false;
    if (countTimer) clearTimeout(countTimer);
    countTimer = setTimeout(refreshCount, 200);
  }
);

const buildAudience = () => {
  if (state.audienceType === 'contacts') {
    return { type: 'contacts', contact_ids: selectedContacts.value.map(c => c.id) };
  }
  if (state.audienceType === 'label') {
    return { type: 'label', labels: selectedLabels.value };
  }
  return { type: 'all' };
};

const handlePrimary = () => {
  if (!canSubmit.value) return;
  // First click on a mass send only arms the confirm; second click fires.
  if (needsConfirm.value && !confirmed.value) {
    confirmed.value = true;
    return;
  }
  handleSubmit();
};

const handleSubmit = async () => {
  isSending.value = true;
  try {
    const payload = {
      audience: buildAudience(),
      title: state.title.trim(),
      body: state.body.trim(),
      category: state.category,
      deep_link: state.deepLink.trim() || null,
      tag: state.tag.trim() || null,
    };
    const result = await store.dispatch('customerNotifications/sendNotification', payload);
    isSending.value = false;
    if (result?.enqueued) {
      useAlert(
        t('CUSTOMER_NOTIFICATIONS.CREATE.BROADCAST_STARTED', { n: result.recipient_count ?? 0 })
      );
    } else {
      useAlert(
        t('CUSTOMER_NOTIFICATIONS.CREATE.SUCCESS', { n: result?.summary?.total || selectedContacts.value.length })
      );
    }
    emit('sent', result);
    close();
  } catch (err) {
    isSending.value = false;
    useAlert(t('CUSTOMER_NOTIFICATIONS.CREATE.ERROR'));
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    position="top"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
  >
    <div class="flex flex-col gap-5">
      <!-- Header -->
      <div class="flex items-start justify-between gap-3">
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('CUSTOMER_NOTIFICATIONS.CREATE.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('CUSTOMER_NOTIFICATIONS.CREATE.SUBTITLE') }}
          </p>
        </div>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-x"
          @click="close"
        />
      </div>

      <!-- Category -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.CATEGORY') }}
        </label>
        <div class="grid grid-cols-4 gap-2">
          <button
            v-for="cat in CATEGORIES"
            :key="cat.value"
            type="button"
            class="flex flex-col items-center justify-center gap-1.5 px-3 py-3 rounded-lg border transition-colors text-xs font-medium"
            :class="
              state.category === cat.value
                ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:border-n-slate-6 hover:text-n-slate-12'
            "
            @click="state.category = cat.value"
          >
            <Icon :icon="cat.icon" class="size-5" />
            <span class="text-center leading-tight">{{ t(cat.labelKey) }}</span>
          </button>
        </div>
      </div>

      <!-- Title -->
      <Input
        v-model="state.title"
        :label="t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.TITLE')"
        :placeholder="t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.TITLE_PLACEHOLDER')"
        :message="titleError || `${state.title.length} / ${TITLE_MAX}`"
        :message-type="titleError ? 'error' : 'info'"
      />

      <!-- Body -->
      <TextArea
        v-model="state.body"
        :label="t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.BODY')"
        :placeholder="t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.BODY_PLACEHOLDER')"
        :max-length="BODY_MAX"
        show-character-count
        :message="bodyError"
        :message-type="bodyError ? 'error' : 'info'"
      />

      <!-- Audience mode -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.AUDIENCE') }}
        </label>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="aud in AUDIENCES"
            :key="aud.value"
            type="button"
            class="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border transition-colors text-xs font-medium"
            :class="
              state.audienceType === aud.value
                ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:border-n-slate-6 hover:text-n-slate-12'
            "
            @click="state.audienceType = aud.value"
          >
            <Icon :icon="aud.icon" class="size-4" />
            <span class="text-center leading-tight">{{ t(aud.labelKey) }}</span>
          </button>
        </div>
      </div>

      <!-- Audience: ALL -->
      <div
        v-if="state.audienceType === 'all'"
        class="flex items-center gap-2 rounded-lg border border-n-amber-6 bg-n-amber-2 px-3 py-2.5 text-sm text-n-slate-12"
      >
        <Icon icon="i-lucide-megaphone" class="size-4 text-n-amber-11 flex-shrink-0" />
        <span>
          {{ t('CUSTOMER_NOTIFICATIONS.CREATE.AUDIENCE.ALL_HINT') }}
          <template v-if="isCounting">…</template>
          <strong v-else-if="audienceCount !== null">
            {{ t('CUSTOMER_NOTIFICATIONS.CREATE.RECIPIENTS_COUNT', { n: audienceCount }) }}
          </strong>
        </span>
      </div>

      <!-- Audience: LABEL -->
      <div v-else-if="state.audienceType === 'label'" class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.LABELS') }}
        </label>
        <div
          v-if="labels.length"
          class="flex flex-wrap gap-1.5 rounded-lg border border-n-weak p-2 max-h-40 overflow-y-auto"
        >
          <button
            v-for="label in labels"
            :key="label.id"
            type="button"
            class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs transition-colors"
            :class="
              isLabelSelected(label.title)
                ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:border-n-slate-6'
            "
            @click="toggleLabel(label.title)"
          >
            <span class="size-2 rounded-full" :style="{ backgroundColor: label.color }" />
            {{ label.title }}
            <Icon
              v-if="isLabelSelected(label.title)"
              icon="i-lucide-check"
              class="size-3 text-n-brand"
            />
          </button>
        </div>
        <p v-else class="text-xs text-n-slate-11 px-1">
          {{ t('CUSTOMER_NOTIFICATIONS.CREATE.NO_LABELS') }}
        </p>
        <p v-if="selectedLabels.length" class="text-xs text-n-slate-11 px-1">
          <template v-if="isCounting">…</template>
          <template v-else-if="audienceCount !== null">
            {{ t('CUSTOMER_NOTIFICATIONS.CREATE.RECIPIENTS_COUNT', { n: audienceCount }) }}
          </template>
        </p>
      </div>

      <!-- Audience: CONTACTS (explicit selection) -->
      <div v-else class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.RECIPIENTS') }}
        </label>
        <!-- Selected chips -->
        <div v-if="selectedContacts.length" class="flex flex-wrap gap-1.5">
          <span
            v-for="contact in selectedContacts"
            :key="contact.id"
            class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-n-alpha-2 text-xs"
          >
            <Avatar
              :name="contact.name || 'Contact'"
              :src="contact.thumbnail || ''"
              :size="16"
              rounded-full
            />
            <span class="font-medium text-n-slate-12">{{ contact.name || contact.email || `#${contact.id}` }}</span>
            <button type="button" class="text-n-slate-11 hover:text-n-ruby-11" @click="removeContact(contact.id)">
              <Icon icon="i-lucide-x" class="size-3" />
            </button>
          </span>
        </div>
        <!-- Search -->
        <Input
          :model-value="searchQuery"
          size="sm"
          :placeholder="t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.RECIPIENTS_PLACEHOLDER')"
          @update:model-value="onSearchInput"
        />
        <!-- Search results -->
        <div v-if="isSearching" class="flex items-center gap-2 text-xs text-n-slate-11 py-2 px-1">
          <Spinner class="size-3" /> {{ t('CUSTOMER_NOTIFICATIONS.CREATE.SEARCHING') }}
        </div>
        <div
          v-else-if="searchQuery && searchResults.length === 0"
          class="text-xs text-n-slate-11 py-2 px-1"
        >
          {{ t('CUSTOMER_NOTIFICATIONS.CREATE.NO_CONTACTS') }}
        </div>
        <div
          v-else-if="searchResults.length"
          class="flex flex-col rounded-lg border border-n-weak max-h-48 overflow-y-auto"
        >
          <button
            v-for="contact in searchResults"
            :key="contact.id"
            type="button"
            class="flex items-center justify-between gap-2 px-2.5 py-2 hover:bg-n-alpha-1 transition-colors text-left border-b border-n-weak last:border-0"
            @click="toggleContact(contact)"
          >
            <div class="flex items-center gap-2 min-w-0">
              <Avatar
                :name="contact.name || 'Contact'"
                :src="contact.thumbnail || ''"
                :size="22"
                rounded-full
              />
              <div class="flex flex-col min-w-0">
                <span class="text-sm font-medium text-n-slate-12 truncate">{{ contact.name || `#${contact.id}` }}</span>
                <span v-if="contact.email" class="text-xs text-n-slate-11 truncate">{{ contact.email }}</span>
              </div>
            </div>
            <Icon
              v-if="isSelected(contact)"
              icon="i-lucide-check"
              class="size-4 text-n-brand flex-shrink-0"
            />
            <Icon v-else icon="i-lucide-plus" class="size-4 text-n-slate-10 flex-shrink-0" />
          </button>
        </div>
        <p v-if="selectedContacts.length === 0 && !searchQuery" class="text-xs text-n-slate-11 px-1">
          {{ t('CUSTOMER_NOTIFICATIONS.CREATE.START_TYPING') }}
        </p>
      </div>

      <!-- Link (optional, https:// or deep link) -->
      <Input
        v-model="state.deepLink"
        :label="t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.DEEP_LINK')"
        placeholder="https://… или vivahelpapp://…"
      />

      <!-- Tag (optional, idempotency) -->
      <Input
        v-model="state.tag"
        :label="t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.TAG')"
        :placeholder="t('CUSTOMER_NOTIFICATIONS.CREATE.FIELDS.TAG_PLACEHOLDER')"
      />

      <!-- Confirm banner for mass sends -->
      <div
        v-if="needsConfirm && confirmed"
        class="flex items-center gap-2 rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-3 py-2.5 text-sm text-n-slate-12"
      >
        <Icon icon="i-lucide-triangle-alert" class="size-4 text-n-ruby-11 flex-shrink-0" />
        <span>{{ t('CUSTOMER_NOTIFICATIONS.CREATE.CONFIRM_HINT', { n: audienceCount ?? '…' }) }}</span>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          type="button"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="close"
        />
        <Button
          :color="needsConfirm && confirmed ? 'ruby' : 'blue'"
          size="sm"
          icon="i-lucide-send"
          :label="submitLabel"
          :is-loading="isSending"
          :disabled="!canSubmit"
          @click="handlePrimary"
        />
      </div>
    </div>
  </Dialog>
</template>
